class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateHandler = this.onQuantityUpdate.bind(this);
    document.addEventListener('afterQuantityUpdate', this.quantityUpdateHandler);
  }

  disconnectedCallback() {
    if (this.quantityUpdateHandler) {
      document.removeEventListener('afterQuantityUpdate', this.quantityUpdateHandler);
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  onQuantityUpdate() {
    this.validateQtyRules();
  }

  validateQtyRules() {
    if(this.input.value == '' || this.input.value == null) {
      this.input.value = this.input.min;
    } else if(parseInt(this.input.value) % parseInt(this.input.step) > 0) {
      this.input.stepUp();
    }
    const value = parseInt(this.input.value);
    if(this.input.max && (value > parseInt(this.input.max))) {
      this.input.value = this.input.max;
    }
    const finalValue = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', finalValue <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', finalValue >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    // Remove "loading" class from any child of .mobile-facets__open-wrapper that has it
    // Also add "hidden" class to any ".loading-overlay__spinner"
    this.querySelectorAll('summary:not(.no-control-menu)').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll('button:not(.no-control-menu)').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    const openWrappers = document.querySelectorAll('.mobile-facets__open-wrapper');
    setTimeout(() => {
      openWrappers.forEach(wrapper => {
        wrapper.querySelectorAll('.loading').forEach(el => el.classList.remove('loading'));
        wrapper.querySelectorAll('.loading-overlay__spinner').forEach(spinner => spinner.classList.add('hidden'));
      });
    }, 2000);
  }

  onKeyUp(event) {
    if(event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if(!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button') || detailsElement.querySelector(':scope > summary'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if(isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        this.hasBackButton ? document.body.classList.add('open-submenu-mobile') : '';
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    if(this.dataset.id) {
      document.body.classList.add(`open-drawer-${this.dataset.id}`);
    }
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.hasBackButton ? document.body.classList.remove('open-submenu-mobile') : '';
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach(submenu => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
    if(this.dataset.id) {
      document.body.classList.remove(`open-drawer-${this.dataset.id}`);
    }
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    this.hasBackButton ? document.body.classList.remove('open-submenu-mobile') : '';
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    closeDetailsInAnimation(detailsElement);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.hasBackButton = true;
  }

  openMenuDrawer(summaryElement) {
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    this.header = this.header || document.querySelector('.section-header');
    this.headerContent = document.querySelector('.header');
    document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.headerContent.getBoundingClientRect().bottom)}px`);
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (event === undefined) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
  }
}

customElements.define('header-drawer', HeaderDrawer);

const animateResizeObserver = new ResizeObserver((entries, observer) => {
  if(window.innerWidth >= 750) {
    entries.forEach(entry => {
      if(!entry.target.inited) {
        entry.target.createObserver();
        observer.unobserve(entry.target);
      }
    });
  }
});

class UseAnimate extends HTMLElement {
  constructor() {
    super();
    if(!window.appearAnimate) return;
    this.inited = false;
  }

  connectedCallback() {
    if(window.innerWidth >= 750) {
      this.createObserver();
    } else {
      animateResizeObserver.observe(this);
    }
  }

  createObserver() {
    this.inited = true;
    Motion.inView(this, (info) => {
      this.showImages();
    });
  }

  showImages() {
    this.classList.add('loaded');
  }
}

customElements.define('use-animate', UseAnimate);

class ToastMessageManager extends HTMLElement {
  constructor() {
    super();
  }

  pushMessage(content, type) {
    var toast = document.createElement('toast-message');
    toast.setContent(content, type);
    this.appendChild(toast);
    setTimeout(() => {
      toast.classList.add('open');
    }, 300);
  }
}

customElements.define('toast-message-manager', ToastMessageManager);

class ToastMessage extends HTMLElement
{
  constructor() {
    super();
    this.waitToCloseTimeout = setTimeout(() => {
      this.close();
    }, 3300);
  }

  connectedCallback() {
    this.appendCloseButton();
  }

  setContent(str, type) {
    this.appendChild(document.createElement('div'));
    this.contentWrapper = this.querySelector('div');
    this.contentWrapper.classList.add('alert');
    this.contentWrapper.textContent = str;
    this.contentWrapper.classList.add(`alert--${type}`);
  }
 
  close() {
    this.classList.remove('open');
    setTimeout(() => {
      this.remove();
    }, 300);
  }

  appendCloseButton() {
    const button = document.createElement('button');
    button.setAttribute('type', 'button');
    button.classList.add('toast__button', 'color-foreground');
    const icon = document.querySelector('.icon-close-sample').cloneNode(true);
    icon.classList.remove('hidden');
    button.appendChild(icon);
    this.onButtonClickHandler = this.onButtonClick.bind(this);
    button.addEventListener('click', this.onButtonClickHandler);
    this.contentWrapper.appendChild(button);
  }

  onButtonClick() {
    if(this.waitToCloseTimeout) {
      clearTimeout(this.waitToCloseTimeout);
    }
    this.close();
  }
}

customElements.define('toast-message', ToastMessage);

function pushMessage(content, type) {
  document.querySelector('toast-message-manager').pushMessage(content, type);
}

function pushSuccessMessage(content) {
  pushMessage(content, 'success');
}

function pushErrorMessage(content) {
  pushMessage(content, 'error');
}

const urlParams = new URLSearchParams(window.location.search); 
const formSuccess = urlParams.has('contact_posted')

if (formSuccess) {
  const successMessage = 'Nachricht erfolgreich versandt! Wir melden uns in kürze bei Dir'; 
  pushMessage(successMessage, 'success');
}

document.addEventListener('click', function(e) {
  if (e.target.classList.contains('ellipsis')) {
    e.target.classList.remove('ellipsis');
  }
});

async function updateShippingTime() {
  const userData = JSON.parse(localStorage.getItem('userLocationData') || '{}');
  const userCountry = userData.country || 'Germany'; // Fallback to Germany
  const isUserInEU = typeof window.isUserInEU !== 'undefined' ? window.isUserInEU : false;

  const currentLocale = document.documentElement.lang || 'de';

  const flagUrl = userData.full.location.country.flag.twemoji;

  document.querySelectorAll('.user-country').forEach(el => {
    const img = `<img src="${flagUrl}" alt="Flag of ${userCountry}" width="14px">`;
    el.innerHTML = img;
  });

  const announcementEl = document.querySelector(".user-country-announcement");
  if (announcementEl && announcementEl.nextElementSibling && !isUserInEU) {
    announcementEl.innerHTML = `We ship to <img src="${flagUrl}" alt="Flag of ${userCountry}" width="14px">`;
    announcementEl.nextElementSibling.textContent = `with DHL and DHL Express`
  }

  const adjustTime = (text) => {
    return text.replace(/\d+-\d+/, (match) => {
      const [start, end] = match.split('-').map(Number);
      if (userCountry === 'Germany') {
        return `${start}-${end}`;
      } else if (isUserInEU) {
        return `${start + 2}-${end + 2}`;
      } else {
        return `${start + 7}-${end + 7}`;
      }
    });
  };

  document.querySelectorAll('.shipping-time').forEach(el => { 
    if (el.getAttribute("data-contains-weeks")) {
      return;
    } else {
      if (el.tagName === 'INPUT') {
        // el.value = adjustTime(el.getAttribute("data-shipping-time"));
      } else {
        // el.textContent = adjustTime(el.getAttribute("data-shipping-time"));
      }
    }
  });
}

function getFlagEmoji(countryCode) {
  return countryCode
    .toUpperCase()
    .replace(/./g, char => String.fromCodePoint(127397 + char.charCodeAt()));
}



document.addEventListener('DOMContentLoaded', () => {
  updateShippingTime()
  document.querySelectorAll('.media--hover-effect:not(.no-link), .full-unstyled-link:not(.footer-block__collapse-link, .no-link)').forEach(link => {
    let startX, startY;

    link.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    link.addEventListener('touchend', function(e) {
      const dx = Math.abs(e.changedTouches[0].clientX - startX);
      const dy = Math.abs(e.changedTouches[0].clientY - startY);
      // only treat it as a tap if finger didn't move more than 10px
      if (dx < 10 && dy < 10) {
        window.location = this.href;
      }
    });
  });
  
  function waitForElement(selector, callback, timeout = 10000) {
    const observer = new MutationObserver((mutations, obs) => {
      const el = document.querySelector(selector);
      if (el) {
        obs.disconnect();
        callback(el);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Optional timeout to stop observing after a certain time
    setTimeout(() => observer.disconnect(), timeout);
  }

  // Usage:
  waitForElement('.ht-tms-single-dropdown__container--language', (el) => {
    const selectedLanguageEl = el.querySelector(".ht-tms-dropdown__selected .ht-tms-option__item")
    let selectedLanguageCode = selectedLanguageEl.getAttribute("data-value").toLowerCase()
    
    if (selectedLanguageCode === 'en') {
      selectedLanguageCode = 'gb';
    }
    const flagImage = document.querySelector('.ht-tms-dropdown__selected .ht-tms-option__flag');
    if (flagImage) {
      flagImage.src = `https://cdn3.hextom.com/img/regional_flag/${selectedLanguageCode}.svg`;
    }
  });
}); 


// Hotjar

document.addEventListener('scroll', function() {
  // Load Hotjar only once after the user has scrolled
  if (!window.hjLoaded) {
      window.hjLoaded = true; // Prevent loading multiple times
      (function(h,o,t,j,a,r){
          h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
          h._hjSettings={hjid:5142604,hjsv:6};
          a=o.getElementsByTagName('head')[0];
          r=o.createElement('script');r.async=1;
          r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
          a.appendChild(r);
      })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
  }
}, { once: true }); // Ensure the event listener is removed after the first scroll

// Zendesk

window.addEventListener('scroll', function() {
  if (!document.getElementById('ze-snippet')) {
    var script = document.createElement('script');
    script.defer = true;
    script.id = 'ze-snippet';
    script.src = 'https://static.zdassets.com/ekr/snippet.js?key=8aad85b3-a248-4400-8890-787e00d4240c';
    document.body.appendChild(script);

    script.onload = () => {
      window.zESettings = {
        webWidget: {
          zIndex: 5,
          position: { horizontal: 'right'}
        }
      };
      
    };
    // Remove the event listener after loading the script
    window.removeEventListener('scroll', arguments.callee);
  }
}, {once: true});  


// truncate-text.js

if (!customElements.get('truncate-text')) {
  customElements.define('truncate-text', class TruncateText extends HTMLElement {
    constructor() {
      super();
      if(!this.dataset.ignore) {
        this.button = this.querySelector('.button--truncate-text');
        this.contentWrapper = this.querySelector('.truncate-text__content-wrapper');
        this.content = this.querySelector('.truncate-text__content');
      }
    }

    connectedCallback() {
      if(this.button) {
        this.onClickButtonHandler = this.onClickButton.bind(this);
        this.button.addEventListener('click', this.onClickButtonHandler);
        this.onResizeHandler = this.onResize.bind(this);
        window.addEventListener('resize', this.onResizeHandler);
        this.calculateHeight();
      }
    }

    disconnectedCallback() {
      this.uninstallEventListener();
    }

    uninstallEventListener() {
      if(this.uninstalled) return;
      if(this.button) {
        this.uninstalled = true;
        this.button.removeEventListener('click', this.onClickButtonHandler);
        window.removeEventListener('resize', this.onResizeHandler);
      }
    }

    onClickButton() {
      this.uninstallEventListener();
      const buttonWrapper = this.button.closest('.truncate-text__button-wrapper');
      const buttonHeight = buttonWrapper.clientHeight;
      this.contentWrapper.classList.remove('truncate-text__content-wrapper-shadow');
      Motion.animate(buttonWrapper, {height: [`${buttonHeight}px`, '0px'], marginTop: '0px'}, {duration: 0.5, easing: `linear`}).finished.then(() => {
        buttonWrapper.remove();
      });
      Motion.animate(this.contentWrapper, { maxHeight: `${this.content.clientHeight}px` }, { duration: 0.5, easing: `cubic-bezier(.03,.93,.97,1)` }).finished.then(() => {
        this.contentWrapper.classList.add('expand');
        this.contentWrapper.removeAttribute('style');
      });
    }

    onResize() {
      this.calculateHeight();
    }

    calculateHeight() {
      if(this.contentWrapper.clientHeight < this.content.clientHeight) {
        this.button.closest('.truncate-text__button-wrapper').classList.remove('hidden');
        this.contentWrapper.classList.add('truncate-text__content-wrapper-shadow');
      }
    }
  });
}


// user-country-logic

document.addEventListener("DOMContentLoaded", () => {
  (function() {
    const CACHE_KEY = 'userLocationData';
    const IPREGISTRY_API_KEY = 'ira_CHxAd7hoEBjx2sQp3Vo101yAS3zb6L3rXv4E';
    const IPREGISTRY_URL = `https://api.ipregistry.co/?key=${IPREGISTRY_API_KEY}`;
  
    window.euCountries = [
      'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic', 'Denmark',
      'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary', 'Ireland', 'Italy',
      'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Poland', 'Portugal',
      'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden'
    ];
  
    function saveCache(data) {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          country: data.location.country.name,
          continent: data.location.continent.name,
          full: data,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.error('Error caching location:', e);
      }
    }
  
    function loadCache() {
      try {
        const cache = localStorage.getItem(CACHE_KEY);
        return cache ? JSON.parse(cache) : null;
      } catch (e) {
        console.error('Error reading cache:', e);
        return null;
      }
    }
  
    function setBodyClass(isInEU) {
      
    }
  
    function handleLocation({ country, continent }) {
      window.userContinent = continent;
      window.isUserInEU = window.euCountries.includes(country) || ["Switzerland", "United Kingdom"].includes(country); 
      setBodyClass(window.isUserInEU);
      localStorage.setItem('isUserInEU', window.isUserInEU ? 'true' : 'false');
  
      const vendorData = window.excludedVendorPerCountry[window.product.vendor] || window.excludedVendorPerCountry[window.product.type];
      if (!vendorData) return;
  
      vendorData.countries = vendorData.countries.map(c => c === "European Union" ? window.euCountries : c).flat();
  
      const shouldExclude = vendorData.excluded;
      const isCountryMatch = vendorData.countries.includes(country) || vendorData.countries.includes(window.product.type);
      const isContinentMatch = vendorData.countries.includes(continent);
  
      console.log("isCountryMatch", isCountryMatch, "isContinentMatch", isContinentMatch)
  
      const shouldBlock = (!shouldExclude && !isCountryMatch && !isContinentMatch) ||
                          (shouldExclude && (isCountryMatch || isContinentMatch));
  
      if (shouldBlock) {
        console.log("should handle")
        observeRecommendations(country, continent);
      }
    }
  
    function observeRecommendations(userCountry, userContinent) {
      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.addedNodes.length) {
            const recommendations = document.querySelector('#product-recommendations.product-recommendations--loaded');
            if (recommendations) {
              observer.disconnect();
              recommendations.querySelectorAll('.grid__item').forEach(item => {
                const vendorEl = item.querySelector('.card__header__product-vendor');
                if (!vendorEl) return;
                const vendorName = vendorEl.textContent.trim();
                const vendorData = window.excludedVendorPerCountry[vendorName];
                if (!vendorData) return;
  
                const { excluded, countries } = vendorData;
                const blockItem = (excluded && (countries.includes(userCountry) || countries.includes(userContinent))) ||
                                  (!excluded && !countries.includes(userCountry) && !countries.includes(userContinent));
                if (blockItem) item.remove();
              });
            }
          }
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  
    function handleErrorMessage(errorMessage = false) {
      const wrapper = document.querySelector('.product-form__error-message-wrapper');
      if (errorMessage && !wrapper) {
        pushErrorMessage(errorMessage);
      }
      if (!wrapper) return;
      const errorElement = wrapper.querySelector('.product-form__error-message');
      wrapper.toggleAttribute('hidden', !errorMessage);
      if (errorMessage) errorElement.innerHTML = errorMessage;
    }
  
    function init() {
      const cachedData = loadCache();
      if (cachedData) {
        handleLocation({ country: cachedData.country, continent: cachedData.continent });
        return;
      } else {
        fetch(IPREGISTRY_URL)
        .then(res => res.json())
        .then(data => {
          saveCache(data);
          handleLocation({ country: data.location.country.name, continent: data.location.continent.name });
        })
        .catch(err => console.error('Error fetching location:', err));
      }
  
    }
  
   init()
  })();
})

// Facets search

function filterFacetItems(input) {
  const query = input.value.trim().toLowerCase();
  const details = input.closest('details');
  const items = details.querySelectorAll('.facets__item');
  let foundMatch = false;

  items.forEach(item => {
    const label = item.getAttribute('data-label');
    const match = label && label.includes(query);

    if (match) {
      item.style.display = 'flex';
      item.classList.remove('hidden'); // If it was .show-more-item.hidden
      foundMatch = true;
    } else {
      item.style.display = 'none';
    }
  }); 

  // Show the <details> if something matches
  if (foundMatch) {
    details.setAttribute('open', true);
  }

  if (query === '') {
    items.forEach((item, index) => {
      item.style.display = '';
      if (index >= 10 && item.classList.contains('show-more-item')) {
        item.classList.add('hidden');
      }
    });
  }
}

document.querySelectorAll(".facet-search-input").forEach(facetSearch => {
  facetSearch.addEventListener("input", () => {
    console.log("changed");
    filterFacetItems(facetSearch);
  })
})



